import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QrCode, Link as LinkIcon, FileText, TicketCheck, KeyRound } from "lucide-react";
import type { PerkWizardFormData } from "./PerkWizard";
import type { PerkDeliveryMethod } from "@/types/generated/cms.generated";

interface DeliveryStepProps {
  form: UseFormReturn<PerkWizardFormData>;
}

const deliveryMethods: { value: PerkDeliveryMethod; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: "PromoCode",
    label: "Promo Code",
    description: "Employee receives a unique or shared promo code",
    icon: <TicketCheck className="w-5 h-5" />,
  },
  {
    value: "QrCode",
    label: "QR Code",
    description: "Employee scans a QR code at the vendor location",
    icon: <QrCode className="w-5 h-5" />,
  },
  {
    value: "ExternalLink",
    label: "External Link",
    description: "Employee is directed to an external URL",
    icon: <LinkIcon className="w-5 h-5" />,
  },
  {
    value: "InstructionsOnly",
    label: "Instructions Only",
    description: "Employee receives text instructions for redemption",
    icon: <FileText className="w-5 h-5" />,
  },
  {
    value: "PinCode",
    label: "Pin Code",
    description: "6-digit numeric code shown to employee at vendor location",
    icon: <KeyRound className="w-5 h-5" />,
  },
];

export function DeliveryStep({ form }: DeliveryStepProps) {
  const deliveryMethod = form.watch("deliveryMethod");

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="deliveryMethod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Delivery Method</FormLabel>
            <div className="grid grid-cols-2 gap-3">
              {deliveryMethods.map((method) => (
                <Card
                  key={method.value}
                  className={`cursor-pointer transition-all ${
                    field.value === method.value
                      ? "border-primary ring-2 ring-primary/20"
                      : "hover:border-muted-foreground/50"
                  }`}
                  onClick={() => field.onChange(method.value)}
                >
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className={`mt-0.5 ${field.value === method.value ? "text-primary" : "text-muted-foreground"}`}>
                      {method.icon}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{method.label}</p>
                      <p className="text-xs text-muted-foreground">{method.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* PromoCode-specific fields */}
      {deliveryMethod === "PromoCode" && (
        <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
          <h4 className="text-sm font-medium">Promo Code Configuration</h4>

          <FormField
            control={form.control}
            name="promoCodePoolType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Code Pool Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || "Unique"}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select pool type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Shared">Shared (one code for everyone)</SelectItem>
                    <SelectItem value="Unique">Unique (one code per employee)</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  Shared codes use a single code for all employees. Unique codes assign one per redemption.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.watch("promoCodePoolType") === "Shared" && (
            <FormField
              control={form.control}
              name="sharedPromoCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Shared Promo Code</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. ATHLYN20" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      )}

      {/* ExternalLink-specific fields */}
      {deliveryMethod === "ExternalLink" && (
        <FormField
          control={form.control}
          name="externalLinkUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>External Link URL</FormLabel>
              <FormControl>
                <Input placeholder="https://vendor.com/offer" {...field} />
              </FormControl>
              <FormDescription>
                The URL employees will be directed to for redemption.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Redemption instructions (all methods) */}
      <FormField
        control={form.control}
        name="redemptionInstructions"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Redemption Instructions</FormLabel>
            <FormControl>
              <Textarea
                placeholder="How should employees use this perk? e.g. Show code at checkout..."
                className="min-h-[80px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="termsAndConditions"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Terms & Conditions (Optional)</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Any terms, limitations, or conditions..."
                className="min-h-[60px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
